import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User, Session } from "@supabase/supabase-js";
import { toast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { VIVU_ADMIN_USER_ID } from "@/lib/constants";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isPasswordRecovery: boolean;
  isAdmin: boolean;
  wasJustLoggedIn: boolean;
  signUp: (email: string, password: string, inviteCode?: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  logout: () => Promise<void>;
  resetPasswordRequest: (email: string) => Promise<{ error: any }>;
  updatePassword: (newPassword: string) => Promise<{ error: any }>;
  resendConfirmationEmail: (email: string) => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const [wasJustLoggedIn, setWasJustLoggedIn] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    // Check early (before Supabase cleans the hash) via sessionStorage flag set in index.html
    try {
      const flag = sessionStorage.getItem('supabase_recovery');
      if (flag === '1') {
        setIsPasswordRecovery(true);
      }
    } catch (e) {}

    // Fallback timeout: stop loading after 5 seconds even if Supabase fails
    const loadingTimeout = setTimeout(() => {
      console.warn('Auth loading timeout - setting isLoading to false');
      setIsLoading(false);
    }, 5000);

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        clearTimeout(loadingTimeout);
        setSession(session);
        setUser(session?.user ?? null);
        
        // Detect password recovery flow
        if (event === 'PASSWORD_RECOVERY') {
          setIsPasswordRecovery(true);
        }
        
        // Clear recovery mode on normal sign in
        if (event === 'SIGNED_IN') {
          // Check if this is NOT a password recovery sign-in
          const recoveryFlag = sessionStorage.getItem('supabase_recovery');
          if (recoveryFlag !== '1') {
            setIsPasswordRecovery(false);
          }
          
          // Refetch all queries after successful login (delayed to prevent interrupting initial dashboard query)
          setTimeout(() => {
            queryClient.invalidateQueries();
          }, 500);
        }
        
        // Handle token refresh errors
        if (event === 'TOKEN_REFRESHED' && !session) {
          console.error('Token refresh failed - clearing all caches');
          localStorage.removeItem('sb-hwjnhvbrkvzumzhakgla-auth-token');
          queryClient.clear();
          toast({
            title: "Sitzung abgelaufen",
            description: "Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.",
            variant: "destructive"
          });
        }
        
        if (event === 'SIGNED_OUT') {
          setIsPasswordRecovery(false);
          localStorage.removeItem('sb-hwjnhvbrkvzumzhakgla-auth-token');
          queryClient.clear();
        }
        
        setIsLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      clearTimeout(loadingTimeout);
      
      // If session fetch fails, clear local storage and force re-login
      if (error) {
        console.error('Session error:', error);
        localStorage.removeItem('sb-hwjnhvbrkvzumzhakgla-auth-token');
        queryClient.clear();
        setSession(null);
        setUser(null);
        
        // Show toast for session errors (except initial load)
        if (error.message && !error.message.includes('No session')) {
          toast({
            title: "Sitzungsfehler",
            description: "Ihre Sitzung ist ungültig. Bitte melden Sie sich erneut an.",
            variant: "destructive"
          });
        }
      } else {
        setSession(session);
        setUser(session?.user ?? null);
      }
      
      setIsLoading(false);
    }).catch((error) => {
      console.error('Failed to get session:', error);
      clearTimeout(loadingTimeout);
      
      // Clear all corrupted session data
      localStorage.removeItem('sb-hwjnhvbrkvzumzhakgla-auth-token');
      queryClient.clear();
      setSession(null);
      setUser(null);
      setIsLoading(false);
      
      // Show toast for critical session failures
      toast({
        title: "Sitzung abgelaufen",
        description: "Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.",
        variant: "destructive"
      });
    });

    return () => {
      clearTimeout(loadingTimeout);
      subscription.unsubscribe();
    };
  }, [queryClient]);

  const signUp = async (email: string, password: string, inviteCode?: string) => {
    // Validate invite code if provided
    if (inviteCode) {
      const { data: invitation, error: inviteError } = await supabase
        .from('invitation')
        .select('*')
        .eq('code', inviteCode)
        .eq('used', false)
        .single();
        
      if (inviteError || !invitation) {
        return { error: { message: 'Ungültiger, bereits verwendeter oder nicht existierender Einladungscode.' } };
      }
      
      if (invitation.used_by_email && invitation.used_by_email !== email) {
        return { error: { message: 'Diese E-Mail-Adresse ist nicht für diesen Einladungscode autorisiert.' } };
      }
      
      if (invitation.used) {
        return { error: { message: 'Dieser Einladungscode wurde bereits verwendet.' } };
      }
    }
    
    const redirectUrl = `${window.location.origin}/`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          invite_code: inviteCode
        }
      }
    });
    
    // Mark invite code as used and create partner entry after successful signup
    if (!error && data.user && inviteCode) {
      const { data: invitation } = await supabase
        .from('invitation')
        .select('*')
        .eq('code', inviteCode)
        .single();

      const { error: updateError } = await supabase
        .from('invitation')
        .update({ 
          used: true, 
          used_by_email: email 
        })
        .eq('code', inviteCode);

      if (updateError) {
        console.error('Error marking invitation as used:', updateError);
      }

      if (invitation && data.user.id) {
        // Check if partner already exists (pre-created by admin)
        const { data: existingPartner } = await supabase
          .from('vertriebspartner')
          .select('id')
          .eq('email', email)
          .maybeSingle();

        if (existingPartner) {
          // Partner already exists - just link the user_id
          const { error: updatePartnerError } = await supabase
            .from('vertriebspartner')
            .update({ user_id: data.user.id })
            .eq('id', existingPartner.id);

          if (updatePartnerError) {
            console.error('Error linking user to existing partner:', updatePartnerError);
          }
        } else {
          // No existing partner - create new entry
          const { error: partnerError } = await supabase
            .from('vertriebspartner')
            .insert({
              user_id: data.user.id,
              email: email,
              vorname: 'Neuer',
              zuname: 'Partner',
              verguetungsschema: invitation.partner_level || 'Client Scout',
              ansprechpartner_id: invitation.vertriebspartner_id,
              mobiltelefon: '',
              company: 'VIVU Finanz & Versicherungsagentur GmbH',
              strasse: '',
              hausnummer: '',
              plz: '',
              ort: '',
            } as any);

          if (partnerError) {
            console.error('Error creating partner entry:', partnerError);
          }
        }
      }
    }
    
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    await queryClient.invalidateQueries();
    
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (!error) {
      setIsPasswordRecovery(false);
      setWasJustLoggedIn(true);
      try { sessionStorage.removeItem('supabase_recovery'); } catch(e) {}
      
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    return { error };
  };

  const logout = async () => {
    localStorage.removeItem('sb-hwjnhvbrkvzumzhakgla-auth-token');
    queryClient.clear();
    
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    if (typeof window !== 'undefined') {
      window.location.href = '/#auth';
    }
  };

  const resetPasswordRequest = async (email: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });

    return { error };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (!error) {
      setIsPasswordRecovery(false);
    }

    return { error };
  };

  const resendConfirmationEmail = async (email: string) => {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: email,
      options: {
        emailRedirectTo: `${window.location.origin}/`
      }
    });

    return { error };
  };

  const value = {
    user,
    session,
    isAuthenticated: !!user,
    isLoading,
    isPasswordRecovery,
    isAdmin: user?.id === VIVU_ADMIN_USER_ID,
    wasJustLoggedIn,
    signUp,
    signIn,
    logout,
    resetPasswordRequest,
    updatePassword,
    resendConfirmationEmail
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
